import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useCities } from '../context/CitiesContext'
import { getSiteFilterOptions } from '../services'
import { infoOptions as legacyInfoOptions } from '../utils'
import { detectCityFromURL } from '../utils/cityDetector'

const GEO_DERIVED_TYPES = ['pseudonym', 'specie', 'family', 'code']

/**
 * Extrae opciones únicas ordenadas de los features del GeoJSON cargado.
 * Retorna formato { title, id } donde id === title (los filtros usan nombres).
 */
function deriveOptionsFromFeatures(type, features) {
  const getValue = f => {
    const p = f.properties
    if (type === 'pseudonym') return p.tooltip?.pseudonym
    if (type === 'specie') return p.tooltip?.specie
    if (type === 'family') return p.tooltip?.family
    if (type === 'code') return p.code
    return null
  }

  const seen = new Set()
  const result = []
  for (const f of features) {
    const v = getValue(f)
    if (v && !seen.has(v)) {
      seen.add(v)
      result.push({ title: v, id: v })
    }
  }
  result.sort((a, b) => a.title.localeCompare(b.title, 'es', { sensitivity: 'accent' }))
  return result
}

/**
 * Hook que provee las opciones del autocomplete para cada tipo de filtro.
 *
 * - pseudonym / specie / family / code → derivadas del GeoJSON en Redux (sin API).
 *   Si hay un tenant seleccionado, filtra solo por sus features.
 * - site → llama al endpoint /site/filter-options?canton=&tenant= (solo al abrir).
 * - otros → delega a las funciones de infoOptions (comportamiento existente).
 */
export const useFilterOptions = type => {
  const [options, setOptions] = useState(null)
  const location = useLocation()
  const { selectedTenant } = useCities()
  const canton = detectCityFromURL(location.pathname)

  const allFeatures = useSelector(state => state.treeFeatures.data?.features || [])

  useEffect(() => {
    if (!type) {
      setOptions(null)
      return
    }

    // Opciones derivadas del GeoJSON local — sin llamada al API
    if (GEO_DERIVED_TYPES.includes(type)) {
      const source = selectedTenant
        ? allFeatures.filter(f => f.properties.tenantSlug === selectedTenant)
        : allFeatures
      setOptions(deriveOptionsFromFeatures(type, source))
      return
    }

    // Sitios filtrados por cantón/tenant — llamada al API contextualizada
    if (type === 'site') {
      let cancelled = false
      getSiteFilterOptions(canton, selectedTenant)
        .then(sites => { if (!cancelled) setOptions(sites) })
        .catch(() => { if (!cancelled) setOptions([]) })
      return () => { cancelled = true }
    }

    // Resto de tipos (feature, phytosanitary, etc.) — comportamiento existente
    const optDef = legacyInfoOptions?.[type]
    if (!optDef?.func) {
      setOptions(null)
      return
    }

    let cancelled = false
    optDef.func()
      .then(res => { if (!cancelled) setOptions(res) })
      .catch(() => { if (!cancelled) setOptions([]) })
    return () => { cancelled = true }
  }, [type, allFeatures, selectedTenant, canton])

  return options
}
